import PropTypes from "prop-types";
import React, { useContext, useEffect, useRef, useState } from "react";
import { defineMessages, FormattedDate, FormattedMessage, injectIntl, intlShape } from "react-intl";
import { Alert, Button, Modal } from "@yoast/ui-library";

import { ErrorPropTypeShape } from "../../../errors/ErrorDisplay";
import { doRequest, prepareInternalRequest } from "shared-frontend/functions/api";

import * as styles from "./styles.module.scss";
import { SubscriptionDetailContext } from "../../../context/SubscriptionDetailContext";
import { GL_NUMBERS } from "shared-frontend/functions/products";

const messages = defineMessages( {
	ariaLabel: {
		id: "addSiteToSubscription.modal.arialabel",
		defaultMessage: "Add another",
	},
	header: {
		id: "addSiteToSubscription.modal.header",
		defaultMessage: "Add {thing}",
		values: { thing: "site" },
	},
	body: {
		id: "addSiteToSubscription.modal.body",
		defaultMessage: "Add an extra {thing} and only pay for the remaining time period.",
		values: { thing: "site" },
	},
	cancel: {
		id: "addSiteToSubscription.modal.cancel",
		defaultMessage: "Cancel",
	},
	confirm: {
		id: "addSiteToSubscription.modal.confirm",
		defaultMessage: "Add {thing}",
		values: { thing: "site" },
	},
	loading: {
		id: "addSiteToSubscription.modal.loading",
		defaultMessage: "Creating order...",
	},
	productHeading: {
		id: "addSiteToSubscription.modal.productSellLine",
		defaultMessage: "Add an extra {thing} for only:",
		values: { thing: "site" },
	},
	productHeadingPrice: {
		id: "addSiteToSubscription.modal.productPrice",
		defaultMessage: "{upgradePrice} (ex. VAT and possible discounts)",
	},
	productBlockName: {
		id: "addSiteToSubscription.modal.productName",
		defaultMessage: "{productName}",
	},
	productBlockBilling: {
		id: "addSiteToSubscription.modal.productBilling",
		defaultMessage: "Next renewal: {nextPaymentDate}",
	},
	productBlockNewLimit: {
		id: "addSiteToSubscription.modal.productNewLimit",
		defaultMessage: "You will upgrade from {limit} to {newLimit} {thing}s",
	},
	refundWarning: {
		id: "addSiteToSubscription.modal.refundWarning",
		defaultMessage: "By clicking the 'Add {thing}' button, I agree to the",
		values: { thing: "site" },
	},
	refundLinkText: {
		id: "addSiteToSubscription.modal.refundLinkText",
		defaultMessage: "refund policy regarding upgrades",
	},
} );

/**
 * Duplicated from sites/my-yoast/client/src/components/Subscription/index.jsx:101
 *
 * @param {Object} product The product to get the text for.
 *
 * @returns {string} The word describing a singular of the thing you add.
 */
export const thingToAddSingular = ( product ) => {
	if ( [
		GL_NUMBERS.yoastSeoAiPlus,
		GL_NUMBERS.premium2025,
		GL_NUMBERS.woocommerceSEO2025,
		GL_NUMBERS.premiumOld,
		GL_NUMBERS.allPlugins,
		GL_NUMBERS.allPluginsPlusAllCourses,
		GL_NUMBERS.local,
		GL_NUMBERS.news,
		GL_NUMBERS.wooCommerce,
		GL_NUMBERS.video,
	].includes( product.glNumber ) ) {
		return "site";
	}

	// for the Google Docs addon, adding a seat will add another user.
	if ( product.glNumber === GL_NUMBERS.googleDocsAddon ) {
		return "seat";
	}

	// Fallback to a generic text.
	return "site";
};

/**
 * Returns the rendered Subscription Upgrade Modal component.
 *
 * @param {Object} props The props to use.
 *
 * @returns {ReactElement} The rendered SubscriptionUpgradeModal component.
 */
function AddSiteToSubscriptionModal( props ) {
	const { subscriptionId, subscription } = useContext( SubscriptionDetailContext );
	const [ upgradePrice, setUpgradePrice ]         = useState( "" );
	const [ isLoading, setIsLoading ]               = useState( false );
	const hasBeenRedirectedToCheckout               = useRef( false );

	// Check the status for the customer when the component is mounted.
	useEffect( () => {
		if ( ! props.isOpen ) {
			return;
		}

		const request = prepareInternalRequest(
			`Subscriptions/calculateExpansionPrice/${ subscriptionId }/${ subscription.subscriptionNumber }`,
			"GET",
			{},
		);

		/**
		 * Fires the request.
		 *
		 * @returns {void}
		 */
		const fireRequest = async() => {
			setIsLoading( true );
			try {
				const { expansionPriceWithCurrency } = await doRequest( request );
				setUpgradePrice( expansionPriceWithCurrency );
			} catch ( err ) {
				// This is an error that the user doesn't need to be informed of. The default will be the subscribe button.
			} finally {
				setIsLoading( false );
			}
		};
		fireRequest();
	}, [ props.isOpen ] );

	/**
	 * Redirects to the checkout.
	 *
	 * @returns {void}
	 */
	function redirectToCheckout() {
		if ( hasBeenRedirectedToCheckout.current ) {
			return;
		}
		hasBeenRedirectedToCheckout.current = true;

		window.location                     = subscription.actions.addSite.url;
	}

	const nextBilling = <FormattedDate
		value={ subscription.validUntil }
		year="numeric"
		month="long"
		day="2-digit"
	/>;
	const thingToAdd  = thingToAddSingular( subscription.product );

	return (
		<Modal
			isOpen={ props.isOpen }
			onClose={ props.onClose }
			aria-label={ messages.ariaLabel.defaultMessage }
		>
			<Modal.Panel>
				<Modal.Title size={ "2" }>
					<FormattedMessage { ...messages.header } values={ { thing: thingToAdd } } />
				</Modal.Title>
				<div className={ styles.subtitle }>
					<FormattedMessage { ...messages.body } values={ { thing: thingToAdd } } />
				</div>
				<div className={ styles.upgradeSubscriptionCard }>

					{ subscription.included.inSubscriptionGroup.products.map( includedProduct => {
						return <div className={ styles.productInfo } key={ subscriptionId }>
							<img
								src={ includedProduct.icon }
								alt={ includedProduct.name }
							/>
							<div>
								<p>
									<FormattedMessage
										{ ...messages.productBlockName }
										values={ { productName: includedProduct.name } }
									/>
								</p>
								<p>
									<strong>
										<FormattedMessage
											{ ...messages.productBlockBilling }
											values={ { nextPaymentDate: nextBilling } }
										/>
									</strong>
								</p>
								<p style={ { opacity: 0.75 } }>
									<i>
										<FormattedMessage
											{ ...messages.productBlockNewLimit }
											values={ {
												newLimit: includedProduct.limit + 1,
												limit: includedProduct.limit,
												thing: thingToAdd,
											} }
										/>
									</i>
								</p>
							</div>
						</div>;
					} ) }

					<div className={ styles.upgradePriceHeader }>
						<p>
							<strong>
								<FormattedMessage { ...messages.productHeading } values={ { thing: thingToAdd } } />
							</strong>
							&nbsp;
							<strong>
								{ ! isLoading && <FormattedMessage
									{ ...messages.productHeadingPrice }
									values={ { upgradePrice: upgradePrice } }
								/> }
							</strong>
						</p>
					</div>

					<div className={ styles.refundWarning }>
						<p>
							<FormattedMessage { ...messages.refundWarning } values={ { thing: thingToAdd } } />
							&nbsp;
							<a href="https://yoast.com/refund-policy/" target="_blank" rel="noopener noreferrer">
								<FormattedMessage { ...messages.refundLinkText } />
							</a>
						</p>
					</div>
				</div>
				{ props.error && <Alert variant="error">{ props.error.message } </Alert> }
				<div className={ styles.actionButtonsContainer }>
					<Button variant="secondary" onClick={ props.onClose }>
						<FormattedMessage { ...messages.cancel } />
					</Button>
					<Button
						variant="primary"
						type="submit"
						onClick={ redirectToCheckout }
					>
						<FormattedMessage { ...messages.confirm } values={ { thing: thingToAdd } } />
					</Button>
				</div>
			</Modal.Panel>
		</Modal>
	);
}

AddSiteToSubscriptionModal.propTypes = {
	intl: intlShape.isRequired,
	isOpen: PropTypes.bool,
	onClose: PropTypes.func.isRequired,
	error: ErrorPropTypeShape,
};

AddSiteToSubscriptionModal.defaultProps = {
	isOpen: false,
};

export default injectIntl( AddSiteToSubscriptionModal );
